import { formatDistanceToNow } from 'date-fns';
import type { ValorantDuoLevelRequirements } from 'generated/openapi';
import { memo, useMemo } from 'react';

import { getDuoLevelProgress } from '@/features/duos/utils/duoLevelProgress';
import { ProfileGameplayIndicator } from '@/features/profile/components/profileGameplayIndicator';
import { ProfileVibeIndicator } from '@/features/profile/components/profileVibeIndicator';
import { DisplayName } from '@/shared/components/displayName';
import { Progress } from '@/shared/components/ui/progress';

interface DuoActivityHeaderProps {
  isMyUserPremium?: boolean;
  partnerDisplayName: string;
  isPartnerPremium?: boolean;
  lastPlayedAt?: string;
  playstyleCompatibility: number;
  vibeCompatibility: number;
  level: number;
  matchCount: number;
  nextLevelRequirements: ValorantDuoLevelRequirements;
}

export const DuoActivityHeader = memo(function DuoActivityHeader({
  isMyUserPremium,
  isPartnerPremium,
  partnerDisplayName,
  lastPlayedAt,
  playstyleCompatibility,
  vibeCompatibility,
  level,
  matchCount,
  nextLevelRequirements,
}: DuoActivityHeaderProps) {
  const timeAgo = useMemo(() => {
    if (!lastPlayedAt) return undefined;
    return formatDistanceToNow(new Date(lastPlayedAt), { addSuffix: true });
  }, [lastPlayedAt]);

  const { matchesAtCurrentLevel, matchesNeededForNextLevel, progressPercent } =
    getDuoLevelProgress(level, matchCount, nextLevelRequirements.matchCount);

  return (
    <div className="bg-cold-600 flex flex-col gap-4 rounded-t-2xl px-2 pt-10 pb-2">
      <div className="flex flex-col items-center gap-2">
        <h2 className="text-cold-100 flex items-center gap-1 text-xl leading-none font-bold">
          <DisplayName name="You" isPremium={isMyUserPremium} /> &{' '}
          <DisplayName name={partnerDisplayName} isPremium={isPartnerPremium} />
        </h2>
        {timeAgo && (
          <p className="text-cold-300 text-xs leading-none font-medium">
            played together {timeAgo}
          </p>
        )}
      </div>
      <div className="flex items-center justify-center gap-2">
        <ProfileGameplayIndicator
          progress={playstyleCompatibility * 100}
          showLabel
        />
        <ProfileVibeIndicator progress={vibeCompatibility * 100} showLabel />
      </div>
      <div className="flex flex-col items-center gap-2">
        <p className="flex items-center gap-1.5">
          <span className="text-cold-100 text-xl leading-tight font-black italic">
            level {level}
          </span>
          <span className="text-cold-300 text-xs leading-none font-medium">
            ({matchesAtCurrentLevel}/{matchesNeededForNextLevel} Matches)
          </span>
        </p>
        <Progress className="w-full" value={progressPercent} />
      </div>
    </div>
  );
});
